/**
 * Canonical VSME portal URL builders and legacy redirect helpers.
 */

export type VsmeDossierQuery = {
  cycle?: number;
  diligence_id?: number;
  note_id?: number;
  status?: string;
};

/** Diligence workbench view state derived from dossier URL query params. */
export type DiligenceViewState = {
  cycle: number | null;
  status: string;
  diligenceId: number | null;
  noteId: number | null;
};

const parsePositiveIntSearchParam = (raw: string | null): number | null => {
  if (raw === null || raw === '') {
    return null;
  }
  const value = Number.parseInt(raw, 10);
  return Number.isInteger(value) && value > 0 ? value : null;
};

const normalizeSearchString = (search: string): string =>
  (search.startsWith('?') ? search.slice(1) : search);

/** Parse dossier deep-link query params from a location search string. */
export const parseDiligenceViewStateFromSearch = (search: string): DiligenceViewState => {
  const params = new URLSearchParams(normalizeSearchString(search));
  const cycle = parsePositiveIntSearchParam(params.get('cycle'));
  if (cycle === null) {
    return {
      cycle: null,
      status: 'all',
      diligenceId: null,
      noteId: null,
    };
  }

  return {
    cycle,
    status: params.get('status') || 'all',
    diligenceId: parsePositiveIntSearchParam(params.get('diligence_id')),
    noteId: parsePositiveIntSearchParam(params.get('note_id')),
  };
};

type DiligenceViewBootstrap = {
  currentCycle?: number | null;
  statusFilter?: string;
  selectedDiligenceId?: number | null;
};

/**
 * Resolve diligence view state from URL search, falling back to bootstrap when the
 * portal injects cycle context before the diligence bundle mounts.
 */
export const resolveDiligenceViewState = (
  search: string,
  bootstrap?: DiligenceViewBootstrap,
): DiligenceViewState => {
  const parsed = parseDiligenceViewStateFromSearch(search);
  if (parsed.cycle !== null || search.includes('cycle=')) {
    return parsed;
  }

  const currentCycle = bootstrap?.currentCycle;
  if (currentCycle !== undefined && currentCycle !== null && currentCycle > 0) {
    return {
      cycle: currentCycle,
      status: bootstrap?.statusFilter ?? 'all',
      diligenceId: bootstrap?.selectedDiligenceId ?? null,
      noteId: parsePositiveIntSearchParam(
        new URLSearchParams(normalizeSearchString(search)).get('note_id'),
      ),
    };
  }

  return parsed;
};

export const parseVsmeDossierQueryFromSearch = (search: string): VsmeDossierQuery => {
  const state = parseDiligenceViewStateFromSearch(search);
  const query: VsmeDossierQuery = {};
  if (state.cycle !== null) {
    query.cycle = state.cycle;
  }
  if (state.status !== '' && state.status !== 'all') {
    query.status = state.status;
  }
  if (state.diligenceId !== null) {
    query.diligence_id = state.diligenceId;
  }
  if (state.noteId !== null) {
    query.note_id = state.noteId;
  }
  return query;
};

const normalizePortalBase = (portalBase: string): string => {
  const trimmed = portalBase.replace(/\/$/, '');
  return trimmed === '' ? '/hub' : trimmed;
};

export const vsmeMissionsListPath = (portalBase = '/hub'): string =>
  `${normalizePortalBase(portalBase)}/vsme/missions`;

export const vsmeMissionNewPath = (companyId?: number, portalBase = '/hub'): string => {
  const base = `${normalizePortalBase(portalBase)}/vsme/missions/new`;
  if (companyId === undefined || companyId <= 0) {
    return base;
  }
  return `${base}?${new URLSearchParams({ company_id: String(companyId) }).toString()}`;
};

export const vsmeMissionSetupPath = (missionId: number, portalBase = '/hub'): string =>
  `${normalizePortalBase(portalBase)}/vsme/missions/${missionId}/setup`;

export const vsmeMissionDetailPath = (missionId: number, portalBase = '/hub'): string =>
  `${normalizePortalBase(portalBase)}/vsme/missions/${missionId}`;

export const vsmeDossierPath = (
  missionId: number,
  portalBase = '/hub',
  query?: VsmeDossierQuery,
): string => {
  const base = `${normalizePortalBase(portalBase)}/vsme/missions/${missionId}/dossier-de-travail`;
  if (query === undefined) {
    return base;
  }

  const params = new URLSearchParams();
  if (query.cycle !== undefined && query.cycle > 0) {
    params.set('cycle', String(query.cycle));
  }
  if (query.status !== undefined && query.status !== '' && query.status !== 'all') {
    params.set('status', query.status);
  }
  if (query.diligence_id !== undefined && query.diligence_id > 0) {
    params.set('diligence_id', String(query.diligence_id));
  }
  if (query.note_id !== undefined && query.note_id > 0) {
    params.set('note_id', String(query.note_id));
  }

  const search = params.toString();
  return search === '' ? base : `${base}?${search}`;
};

/** Relative portal paths (no portal base prefix) for `navigateTo` / `navigateInPortal`. */
export const vsmeMissionsListRelativePath = (): string => '/vsme/missions';

export const vsmeMissionNewRelativePath = (companyId?: number): string => {
  if (companyId === undefined || companyId <= 0) {
    return '/vsme/missions/new';
  }
  return `/vsme/missions/new?${new URLSearchParams({ company_id: String(companyId) }).toString()}`;
};

export const vsmeMissionSetupRelativePath = (missionId: number): string =>
  `/vsme/missions/${missionId}/setup`;

export const vsmeMissionDetailRelativePath = (missionId: number): string =>
  `/vsme/missions/${missionId}`;

export const vsmeDossierRelativePath = (missionId: number, query?: VsmeDossierQuery): string => {
  const base = `/vsme/missions/${missionId}/dossier-de-travail`;
  if (query === undefined) {
    return base;
  }

  const params = new URLSearchParams();
  if (query.cycle !== undefined && query.cycle > 0) {
    params.set('cycle', String(query.cycle));
  }
  if (query.status !== undefined && query.status !== '' && query.status !== 'all') {
    params.set('status', query.status);
  }
  if (query.diligence_id !== undefined && query.diligence_id > 0) {
    params.set('diligence_id', String(query.diligence_id));
  }
  if (query.note_id !== undefined && query.note_id > 0) {
    params.set('note_id', String(query.note_id));
  }

  const search = params.toString();
  return search === '' ? base : `${base}?${search}`;
};

export type LegacyVsmeRedirect = {
  pathname: string;
  search: string;
};

const normalizePathname = (value: string): string => {
  if (value.length > 1 && value.endsWith('/')) {
    return value.slice(0, -1);
  }
  return value;
};

/**
 * Map legacy VSME portal URLs to canonical mission-first URLs (sync redirects only).
 * Report-id dossier redirects require async mission resolution elsewhere.
 */
export const resolveLegacyVsmeRedirect = (
  pathname: string,
  search: string,
  portalBase = '/hub',
): LegacyVsmeRedirect | null => {
  const base = normalizePortalBase(portalBase);
  const current = normalizePathname(pathname);
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);

  const legacyMissionMatch = current.match(new RegExp(`^${base.replace(/[/\\^$*+?.()|[\]{}]/g, '\\$&')}/missions/(\\d+)$`));
  if (legacyMissionMatch) {
    return {
      pathname: `${base}/vsme/missions/${legacyMissionMatch[1]}`,
      search: search,
    };
  }

  const legacyMissionVsmeMatch = current.match(
    new RegExp(`^${base.replace(/[/\\^$*+?.()|[\]{}]/g, '\\$&')}/missions/(\\d+)/vsme$`),
  );
  if (legacyMissionVsmeMatch) {
    return {
      pathname: `${base}/vsme/missions/${legacyMissionVsmeMatch[1]}`,
      search: search,
    };
  }

  if (current === `${base}/vsme/reports`) {
    const create = params.get('create');
    const companyId = params.get('company_id');
    const missionId = params.get('mission_id');

    if (create === '1' && missionId !== null && missionId !== '') {
      return {
        pathname: `${base}/vsme/missions/${missionId}/setup`,
        search: '',
      };
    }

    if (create === '1' && companyId !== null && companyId !== '') {
      return {
        pathname: `${base}/vsme/missions/new`,
        search: `?${new URLSearchParams({ company_id: companyId }).toString()}`,
      };
    }

    return {
      pathname: `${base}/vsme/missions`,
      search: '',
    };
  }

  return null;
};

export const parseLegacyVsmeReportDossierId = (
  pathname: string,
  portalBase = '/hub',
): number | null => {
  const base = normalizePortalBase(portalBase);
  const current = normalizePathname(pathname);
  const match = current.match(
    new RegExp(`^${base.replace(/[/\\^$*+?.()|[\]{}]/g, '\\$&')}/vsme/reports/(\\d+)$`),
  );
  if (!match) {
    return null;
  }

  const reportId = Number.parseInt(match[1], 10);
  return Number.isInteger(reportId) && reportId > 0 ? reportId : null;
};

export const parseVsmeMissionIdFromPath = (
  pathname: string,
  portalBase = '/hub',
): number | null => {
  const base = normalizePortalBase(portalBase);
  const current = normalizePathname(pathname);
  const escapedBase = base.replace(/[/\\^$*+?.()|[\]{}]/g, '\\$&');

  const detailMatch = current.match(new RegExp(`^${escapedBase}/vsme/missions/(\\d+)$`));
  if (detailMatch) {
    const id = Number.parseInt(detailMatch[1], 10);
    return Number.isInteger(id) && id > 0 ? id : null;
  }

  const setupMatch = current.match(new RegExp(`^${escapedBase}/vsme/missions/(\\d+)/setup$`));
  if (setupMatch) {
    const id = Number.parseInt(setupMatch[1], 10);
    return Number.isInteger(id) && id > 0 ? id : null;
  }

  const dossierMatch = current.match(
    new RegExp(`^${escapedBase}/vsme/missions/(\\d+)/dossier-de-travail$`),
  );
  if (dossierMatch) {
    const id = Number.parseInt(dossierMatch[1], 10);
    return Number.isInteger(id) && id > 0 ? id : null;
  }

  return null;
};

export const isVsmeMissionSetupPath = (pathname: string, portalBase = '/hub'): boolean => {
  const base = normalizePortalBase(portalBase);
  const current = normalizePathname(pathname);
  return (
    current === `${base}/vsme/missions/new`
    || /\/vsme\/missions\/\d+\/setup$/.test(current)
    || (current === `${base}/vsme/reports` && new URLSearchParams(window.location.search).get('create') === '1')
  );
};

/** True when pathname is the hub mission dossier route (query string ignored). */
export const isPortalMissionDossierPath = (pathname: string, portalBase = '/hub'): boolean => {
  const base = normalizePortalBase(portalBase);
  const current = normalizePathname(pathname);
  const escapedBase = base.replace(/[/\\^$*+?.()|[\]{}]/g, '\\$&');
  return new RegExp(`^${escapedBase}/vsme/missions/\\d+/dossier-de-travail$`).test(current);
};
